import {
  useMemo,
  useState
} from 'react';
import {
  SimpleGrid,
  Skeleton,
  Flex,
} from '@mantine/core';
import { theme } from '@huspy/forge';
import { useDisclosure, useLocalStorage } from '@mantine/hooks';
import useListOpportunities from '@modules/opportunities/entities/opportunity/query/useListOpportunities';
import { useTranslation } from 'react-i18next';
import { ClientDetailsRoute } from '@modules/clients/presentation/routes';
import { BankApplicationStatus } from '@modules/opportunities/entities/bankApplication/bankApplication.entity';
import { OPPORTUNITY_STATUS } from '@modules/opportunities/entities/opportunity/const';
import { CaseSlido } from '@modules/opportunities/presentation/pages/CaseCreation/features/CaseSlido';
import useClientDetails from '@modules/clients/entities/client/query/useClientDetails';
import { useNavigate } from '@tanstack/react-router';
import { CaseCreationBasicInfoRoute, CaseCreationSubmissionRootRoute } from '@modules/opportunities/presentation/routes';
import { CaseDetailsSlido } from '@modules/opportunities/presentation/pages/CaseCreation/features/CaseDetailsSlido';
import { BANK_APPLICATION_STATUS } from '@modules/opportunities/entities/bankApplication/const';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';
import ClientCaseCard from '@modules/clients/presentation/v1/pages/Details/components/ClientCaseCard';
import { EmptyTemplateTab } from '@modules/clients/presentation/v1/pages/Details/components/EmptyTemplateTab';
import { ClientDetailsDTO } from '@modules/clients/entities/client/api/dto/clientDetails.dto';
import usePersistUser from '@modules/core/hooks/usePersistUser';

const CreateCaseSlido = (
  { isFeatureActive, clientData }: { clientData?: ClientDetailsDTO, isFeatureActive: (featureName: string) => boolean }
) => {
  if (!isFeatureActive('opportunities.caseCreation') || !clientData) {
    return null;
  }

  const existingClientData = {
    firstName: clientData.first_name,
    lastName: clientData.last_name,
    id: clientData.id,
    value: clientData.id,
    mobile: clientData.mobile,
    email: clientData.email,
  };

  return (
    <CaseSlido
      hideTabs
      tab='existingClient'
      existingClientData={ existingClientData }
    />
  );
};

export const ClientCasesTab = () => {
  const { id } = ClientDetailsRoute.useParams();
  const { user } = usePersistUser();
  const { data: clientData } = useClientDetails(id, user?.brokerageId);
  const { data, isLoading } = useListOpportunities(id);
  const [sidebarCollapsed] = useLocalStorage({
    key: 'sidebarCollapsed',
    getInitialValueInEffect: false,
    defaultValue: false,
  });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isFeatureActive } = useFeatureFlags();
  const [selectedBankApp, setSelectedBankApp] = useState({ opportunityId: '', bankApplicationId: '' });
  const [isOpen, { open, close }] = useDisclosure(false);

  const allBankApplications = useMemo(
    () => data?.opportunities.map((opp) => {
      if (opp.status === OPPORTUNITY_STATUS.draft && opp.bank_applications[0]) {
        return {
          ...opp,
          bank_applications: [opp.bank_applications[0]],
        };
      }

      return opp;
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  if (!allBankApplications?.length && !isLoading) {
    return (
      <Flex direction='column' rowGap={ theme.spacing.xl }>
        <EmptyTemplateTab
          heading={ t('tabs.cases.placeholder.heading') }
          subHeading={ t('tabs.cases.placeholder.subHeading') }
        />
        <Flex justify='center'>
          <CreateCaseSlido isFeatureActive={ isFeatureActive } clientData={ clientData } />
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex direction='column' rowGap={ theme.spacing.xl }>
      { !isLoading && (
        <Flex justify='flex-end'>
          <CreateCaseSlido isFeatureActive={ isFeatureActive } clientData={ clientData } />
        </Flex>
      )}
      <SimpleGrid cols={ sidebarCollapsed ? 3 : 2 } spacing={ theme.spacing.lg }>
        {isLoading ? (
          <>
            <Skeleton h={ 254 } radius='4px' />
            <Skeleton h={ 254 } radius='4px' />
            <Skeleton h={ 254 } radius='4px' />
          </>
        ) : (
          <>
            {allBankApplications?.map((opportunity) => (
              opportunity.bank_applications.map((bankApp) => (
                <ClientCaseCard
                  key={ bankApp.bank_application_external_id }
                  // eslint-disable-next-line max-len
                  createdBy={ `${opportunity?.referrer_first_name ?? ''} ${opportunity?.referrer_last_name ?? ''}`.trim() }
                  { ...bankApp }
                  total_applications={ opportunity.bank_applications.length }
                  onClick={ (bankApplicationId) => {
                    if (
                      opportunity.status === OPPORTUNITY_STATUS.draft
                    ) {
                      navigate({
                        to: CaseCreationBasicInfoRoute.to,
                        params: { opportunityId: opportunity.opportunity_external_id },
                      });
                      return;
                    }

                    if (bankApp.status === BANK_APPLICATION_STATUS.draft) {
                      navigate({
                        to: CaseCreationSubmissionRootRoute.to,
                        params: { opportunityId: bankApp.opportunity_external_id },
                      });
                    } else {
                      setSelectedBankApp({
                        bankApplicationId,
                        opportunityId: bankApp.opportunity_external_id,
                      });
                      open();
                    }
                  } }
                />
              ))
            ))}

            {allBankApplications?.map((opportunity) =>
              (opportunity.bank_applications.length === 0
                ? (
                  <ClientCaseCard
                    key={ opportunity.opportunity_external_id }
                    // eslint-disable-next-line max-len
                    createdBy={ `${opportunity?.referrer_first_name ?? ''} ${opportunity?.referrer_last_name ?? ''}`.trim() }
                    amount={ opportunity.amount }
                    created_at={ opportunity.created_at }
                    status={ opportunity.status.toLowerCase() as BankApplicationStatus }
                    property_value={ opportunity.property_value || 0 }
                    currency={ opportunity.currency }
                    total_applications={ 0 }
                    onClick={ () => {
                      if (
                        opportunity.status === OPPORTUNITY_STATUS.draft
                      ) {
                        navigate({
                          to: CaseCreationBasicInfoRoute.to,
                          params: { opportunityId: opportunity.opportunity_external_id },
                        });
                      }
                    } }
                    additional_comment={ null }
                    opportunity_external_id=''
                    bank_application_external_id=''
                    is_primary={ false }
                    down_payment_percentage={ 0 }
                    bank_submission_channel_type={ null }
                    bank_details={ {
                      name: '',
                      logo: '',
                      icon: '',
                      external_id: '',
                      selected_rate: {
                        external_id: undefined,
                        interest_rate_type: undefined,
                        fixed_rate: undefined,
                        fixed_until: undefined,
                        variable_rate_addition: undefined,
                        eibor_type: undefined,
                        is_manual_rate: undefined,
                      },
                    } }
                    documents={ [] }
                    referrer_first_name=''
                    referrer_last_name=''
                  />
                ) : null))}

          </>
        )}
      </SimpleGrid>
      <CaseDetailsSlido
        opportunityId={ selectedBankApp.opportunityId }
        bankApplicationId={ selectedBankApp.bankApplicationId }
        isOpen={ isOpen }
        close={ close }
      />
    </Flex>
  );
};
